import { useCallback, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { AreaTimer } from "@/components/AreaTimer";

const fmt = (secs) => {
  const s = Math.max(0, secs | 0);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

// Slim, collapsible map of a segment: a Timer section and an ordered
// "what happens" section. Timers stay mounted while collapsed so the compact
// readout keeps ticking. Right rail on desktop, top bar on mobile.
export const SegmentGuide = ({
  area,
  timers = [],
  segments = [],
  stepIndex,
  onSelectStep,
}) => {
  const [open, setOpen] = useState(false);
  const [timerOpen, setTimerOpen] = useState(true);
  const [stepsOpen, setStepsOpen] = useState(true);
  const [reports, setReports] = useState({});

  const report = useCallback((label, data) => {
    setReports((r) => {
      const prev = r[label];
      if (prev && prev.status === data.status && prev.remaining === data.remaining)
        return r;
      return { ...r, [label]: data };
    });
  }, []);

  const readout = useMemo(() => {
    const entries = timers.map((t) => reports[t.label]).filter(Boolean);
    if (!entries.length) {
      const first = timers[0];
      return first ? fmt(first.duration_minutes * 60) : "";
    }
    const active =
      entries.find((e) => e.status === "running") ||
      entries.find((e) => e.status === "paused") ||
      entries.find((e) => e.status !== "done") ||
      entries[entries.length - 1];
    return fmt(active.remaining);
  }, [reports, timers]);

  const groups = useMemo(() => {
    if (timers.length > 1) {
      return timers.map((t) => ({
        label: t.label,
        items: segments.filter((s) => s.timer === t.label),
      }));
    }
    return [{ label: null, items: segments }];
  }, [timers, segments]);

  const sectionHeader = "w-full flex items-center justify-between py-3 text-left";

  return (
    <aside
      data-testid="segment-guide"
      className="bg-white border border-sand min-w-0 box-border"
    >
      {/* Collapsed / summary row, always visible */}
      <button
        type="button"
        data-testid="segment-guide-toggle"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 min-w-0"
      >
        <span className="mono-label text-ink/70 truncate min-w-0">{area.label}</span>
        <span className="flex items-center gap-3 shrink-0">
          <span data-testid="segment-guide-readout" className="mono-label text-teal tabular-nums">
            {readout}
          </span>
          <ChevronDown
            size={16}
            className={`text-ink/50 transition-transform duration-150 ${
              open ? "rotate-180" : ""
            }`}
          />
        </span>
      </button>

      {/* Section 1: Timer header, shown only when expanded */}
      <div className={`border-t border-sand px-4 ${open ? "" : "hidden"}`}>
        <button
          type="button"
          data-testid="segment-timer-toggle"
          onClick={() => setTimerOpen((o) => !o)}
          className={sectionHeader}
        >
          <span className="mono-label text-ink/60">Timer</span>
          <ChevronDown
            size={14}
            className={`text-ink/40 transition-transform duration-150 ${
              timerOpen ? "rotate-180" : ""
            }`}
          />
        </button>
      </div>

      {/* Timers: always mounted so the readout keeps ticking; hidden unless open + timerOpen */}
      <div
        className={`px-4 pb-4 space-y-3 min-w-0 ${open && timerOpen ? "" : "hidden"}`}
      >
        {timers.map((t) => (
          <AreaTimer
            key={t.label}
            label={t.label}
            durationMinutes={t.duration_minutes}
            onStart={t.onStart}
            onTick={t.onTick}
            onReport={report}
          />
        ))}
      </div>

      {/* Section 2: What happens in this segment */}
      {open && (
        <div className="border-t border-sand px-4">
          <button
            type="button"
            data-testid="segment-steps-toggle"
            onClick={() => setStepsOpen((o) => !o)}
            className={sectionHeader}
          >
            <span className="mono-label text-ink/60">
              What happens in this segment
            </span>
            <ChevronDown
              size={14}
              className={`text-ink/40 transition-transform duration-150 ${
                stepsOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {stepsOpen && (
            <div data-testid="segment-steps" className="pb-4 space-y-4">
              {groups.map((group) => (
                <div key={group.label || "all"}>
                  {group.label && (
                    <p className="mono-label text-ink/40 mb-2">{group.label}</p>
                  )}
                  <ol className="space-y-1">
                    {group.items.map((item) => {
                      const n = segments.indexOf(item);
                      const done = item.step < stepIndex;
                      const current = item.step === stepIndex;
                      return (
                        <li key={n}>
                          <button
                            type="button"
                            data-testid={`segment-step-${n}`}
                            onClick={() => onSelectStep(item.step)}
                            className={`w-full text-left py-2 pl-3 border-l-2 transition-colors duration-150 ${
                              current
                                ? "border-orange bg-orange/5"
                                : "border-sand hover:border-orange/60"
                            }`}
                          >
                            <span className="flex items-baseline gap-2">
                              <span
                                className={`mono-label ${
                                  current ? "text-orange-dark" : "text-ink/40"
                                }`}
                              >
                                {done ? "✓" : String(n + 1).padStart(2, "0")}
                              </span>
                              <span
                                className={`text-sm font-medium ${
                                  current ? "text-orange-dark" : "text-ink/80"
                                }`}
                              >
                                {item.name}
                              </span>
                            </span>
                            <span className="mono-label text-ink/40 block pl-6 mt-0.5">
                              about {item.minutes} minutes
                            </span>
                            <span className="text-xs text-ink/60 block pl-6 mt-1">
                              {item.line}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </aside>
  );
};
